/**
 * Phase 3 — Fuel Intelligence module pages.
 * Single file exporting 8 sub-pages.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Truck, Wrench, Activity, Droplet, AlertTriangle, UsersRound, PieChart, FileText,
  Plus, X, Loader2, Gauge, Fuel, Navigation2, Download, TrendingUp, Car,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPost, apiPut, apiDel, api } from '../../lib/api';
import { formatINR } from '../../lib/format';
import LeafletMap from '../../components/LeafletMap';

const GPS_STATUS_STYLE = {
  unconfigured: 'bg-slate-100 text-slate-500 border-slate-200',
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  offline: 'bg-amber-50 text-amber-700 border-amber-200',
  inactive: 'bg-red-50 text-red-700 border-red-200',
};

const GpsStatusBadge = ({ status }) => (
  <span data-testid="gps-status-badge"
    className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-display font-bold uppercase tracking-wider border ${GPS_STATUS_STYLE[status] || GPS_STATUS_STYLE.unconfigured}`}>
    {status || 'unconfigured'}
  </span>
);

const GpsSection = ({ form, set, existing }) => {
  const inp = 'w-full px-3 py-2 border border-slate-300 rounded-sm text-sm';
  const lastSeen = existing?.gps_last_seen
    ? new Date(existing.gps_last_seen).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
    : '—';
  const hasPos = existing?.gps_lat != null && existing?.gps_lng != null;
  return (
    <div className="col-span-full mt-2 border-t border-slate-200 pt-3" data-testid="gps-section">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-display font-bold text-sm flex items-center gap-1.5"><Navigation2 size={14} className="text-[#1F7A6E]" /> GPS Tracking</h4>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!form.gps_enabled} onChange={(e) => set('gps_enabled', e.target.checked)} data-testid="gps-enabled-toggle" />
          <span className="text-xs font-display font-bold uppercase tracking-wider text-slate-600">{form.gps_enabled ? 'Enabled' : 'Disabled'}</span>
        </label>
      </div>
      {form.gps_enabled && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Device ID (IMEI / Serial)"><input value={form.gps_device_id || ''} onChange={(e) => set('gps_device_id', e.target.value)} placeholder="e.g. 86xxxxxxxxxxxxx" className={inp} data-testid="gps-device-id" /></Field>
          <Field label="Provider"><input value={form.gps_provider || ''} onChange={(e) => set('gps_provider', e.target.value)} placeholder="e.g. Trakzee, Letstrack — configure later" className={inp} data-testid="gps-provider" /></Field>
          <Field label="SIM Number"><input value={form.gps_sim_number || ''} onChange={(e) => set('gps_sim_number', e.target.value)} placeholder="SIM in the GPS device" className={inp} data-testid="gps-sim" /></Field>
          <Field label="Status">
            <div className="flex items-center gap-3 py-1.5">
              <GpsStatusBadge status={existing?.gps_status || (form.gps_device_id ? 'inactive' : 'unconfigured')} />
              <span className="text-xs text-slate-500">Last seen: {lastSeen}</span>
            </div>
          </Field>
          {form.gps_enabled && !(form.gps_device_id || '').trim() && (
            <div className="col-span-full text-xs text-amber-600">Enter Device ID to activate GPS tracking.</div>
          )}
          {hasPos && (
            <div className="col-span-full">
              <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-400 mb-1">Last Known Location</div>
              <LeafletMap markers={[{ id: existing.id, lat: existing.gps_lat, lng: existing.gps_lng, color: '#1F7A6E', label: form.vehicle_number }]} height="200px" zoom={14} center={[existing.gps_lat, existing.gps_lng]} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const FUEL_TYPES = ['Diesel', 'Petrol', 'CNG', 'Electric', 'LPG'];
const VEHICLE_TYPES = ['Transit Mixer', 'Truck', 'Tipper', 'Water Tanker', 'Other'];
const EQUIPMENT_TYPES = ['Batching Plant', 'Concrete Pump', 'Wheel Loader', 'Compressor', 'Generator (DG Set)', 'Weighbridge', 'Other'];

const Tile = ({ label, value, sub, accent = 'slate' }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-4">
    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-display">{label}</div>
    <div className={`text-2xl font-bold mt-1 text-${accent}-700`}>{value}</div>
    {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] uppercase tracking-widest font-display text-slate-500">{label}</label>
    {children}
  </div>
);


// ────────────────────────────────────────────────────────────
// 2.1 Dashboard
// ────────────────────────────────────────────────────────────
export function FuelDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { apiGet('/fuel/dashboard').then(setD).catch(() => {}); }, []);
  if (!d) return <div className="p-8 text-slate-500">Loading…</div>;

  const CHART_COLORS = ['#1F7A6E','#6366f1','#f59e0b','#10b981','#ef4444','#8b5cf6'];

  return (
    <div className="p-6 space-y-5">
      <PageHeader title="Fuel Dashboard" subtitle="Vehicle + equipment fuel consumption at the plant" icon={<Fuel size={18} />} />

      {/* ── KPI Strip ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Fuel · This Month" value={`${d.this_month_litres} L`} accent="amber" />
        <Tile label="Cost · This Month" value={formatINR(d.this_month_cost)} sub={`${d.cost_delta_pct >= 0 ? '+' : ''}${d.cost_delta_pct}% vs last month`} accent="indigo" />
        <Tile label="Open Alerts" value={d.active_alerts} accent="rose" sub={d.active_alerts > 0 ? 'Click Alerts tab →' : undefined} />
        <Tile label="Night Movement · 7d" value={d.night_alerts_week} accent="violet" />
      </div>

      {/* ── Daily Trend (last 7 days) ── */}
      {(d.daily_trend || []).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <h3 className="font-display font-bold text-sm mb-3 flex items-center gap-2">
            <TrendingUp size={14} className="text-[#1F7A6E]" /> Daily Consumption · Last 7 Days
          </h3>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={d.daily_trend} margin={{ top: 0, right: 0, left: -10, bottom: 0 }}>
              <XAxis dataKey="_id" tick={{ fontSize: 10 }} tickFormatter={v => v?.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} unit="L" />
              <Tooltip formatter={(v) => [`${v.toFixed(1)} L`, 'Litres']} labelFormatter={l => `Date: ${l}`} />
              <Bar dataKey="litres" fill="#1F7A6E" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Equipment + Vehicles + Fuel Type ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <h3 className="font-display font-bold text-sm mb-3">Top 5 High-Consumption Equipment</h3>
          <div className="space-y-1.5">
            {(d.top_equipment || []).map((e, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span>#{i + 1} {e.name || e._id || '—'}</span>
                <span className="font-bold tabular-nums">{e.litres?.toFixed(1)} L</span>
              </div>
            ))}
            {!d.top_equipment?.length && <div className="text-xs text-slate-400 italic">No equipment logs.</div>}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <h3 className="font-display font-bold text-sm mb-3 flex items-center gap-2">
            <Car size={13} className="text-[#1F7A6E]" /> Top Vehicles · This Month
          </h3>
          <div className="space-y-1.5">
            {(d.top_vehicles || []).map((v, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="truncate max-w-[60%]">#{i + 1} {v.vehicle_name}</span>
                <span className="tabular-nums text-slate-600">{v.litres?.toFixed(1)} L · {formatINR(v.amount)}</span>
              </div>
            ))}
            {!d.top_vehicles?.length && <div className="text-xs text-slate-400 italic">No vehicle logs this month.</div>}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <h3 className="font-display font-bold text-sm mb-3">Fuel Type Breakdown · This Month</h3>
          {(d.fuel_type_breakdown || []).length > 0 ? (
            <div className="flex gap-4 items-center">
              <div className="flex-1">
                <ResponsiveContainer width="100%" height={120}>
                  <BarChart data={d.fuel_type_breakdown} layout="vertical" margin={{ top: 0, right: 30, left: 0, bottom: 0 }}>
                    <XAxis type="number" tick={{ fontSize: 10 }} unit="L" />
                    <YAxis type="category" dataKey="_id" tick={{ fontSize: 10 }} width={55} />
                    <Tooltip formatter={(v) => [`${v.toFixed(1)} L`, 'Litres']} />
                    <Bar dataKey="litres" radius={[0, 3, 3, 0]}>
                      {d.fuel_type_breakdown.map((_, idx) => (
                        <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <div className="text-xs text-slate-400 italic">No logs this month.</div>
          )}
        </div>
      </div>

      {/* ── Tank Levels ── */}
      <div className="bg-white border border-slate-200 rounded-sm p-4">
        <h3 className="font-display font-bold text-sm mb-3 flex items-center gap-2"><Droplet size={14} className="text-cyan-500" /> Tank Levels</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {(d.tanks || []).map((t) => {
            const pct = t.capacity_l ? Math.min(100, Math.round((t.current_level_l / t.capacity_l) * 100)) : 0;
            const color = pct < 25 ? 'bg-rose-500' : pct < 50 ? 'bg-amber-500' : 'bg-emerald-500';
            return (
              <div key={t.id} className="border border-slate-200 rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-widest font-display text-slate-500">{t.tank_id}</div>
                <div className="text-xs truncate">{t.location || '—'}</div>
                <div className="mt-2 h-3 bg-slate-100 rounded">
                  <div className={`h-3 ${color} rounded`} style={{ width: `${pct}%` }} />
                </div>
                <div className="mt-1 text-xs flex justify-between"><span>{t.current_level_l?.toFixed(0)} L</span><span className="text-slate-400">/ {t.capacity_l?.toFixed(0)} L</span></div>
              </div>
            );
          })}
          {!d.tanks?.length && <div className="text-xs text-slate-400 italic col-span-4">No tanks configured.</div>}
        </div>
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────
// 2.2 / 2.3 Master tables — Vehicle & Equipment (shared pattern)
// ────────────────────────────────────────────────────────────
function MasterTable({ title, icon, fetchUrl, deleteUrl, columns, FormComponent }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const refresh = () => apiGet(fetchUrl).then(d => setRows(d.items || []));
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);
  const remove = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    try { await apiDel(`${deleteUrl}/${id}`); toast.success('Deleted'); refresh(); } catch { toast.error('Failed'); }
  };
  return (
    <div className="p-6 space-y-4">
      <PageHeader title={title} icon={icon}
        actions={<button onClick={() => setShowNew(true)} className="px-3 py-1.5 bg-[#1F7A6E] hover:bg-[#14504E] text-white rounded-sm text-xs font-display font-bold flex items-center gap-1.5"><Plus size={14} /> Add</button>}
      />
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-display">
            <tr>{columns.map(c => <th key={c.k} className={`px-3 py-2 ${c.right ? 'text-right' : 'text-left'}`}>{c.label}</th>)}<th className="px-3 py-2"></th></tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                {columns.map(c => (
                  <td key={c.k} className={`px-3 py-2 text-xs ${c.right ? 'text-right tabular-nums' : ''}`}>
                    {c.k === 'project_name'
                      ? (r.project_name || r.office_location_name || '—')
                      : (c.fmt ? c.fmt(r[c.k]) : r[c.k] ?? '—')}
                  </td>
                ))}
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setEditing(r)} className="text-[10px] text-[#1F7A6E] hover:underline mr-2">Edit</button>
                  <button onClick={() => remove(r.id)} className="text-[10px] text-rose-600 hover:underline">Del</button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={columns.length + 1} className="text-center text-slate-400 italic py-6">No records yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {(showNew || editing) && (
        <FormComponent existing={editing} onClose={() => { setShowNew(false); setEditing(null); }} onSaved={() => { setShowNew(false); setEditing(null); refresh(); }} />
      )}
    </div>
  );
}


export function VehicleMaster() {
  return <MasterTable
    title="Vehicle Master" icon={<Truck size={18} />}
    fetchUrl="/fuel/vehicles" deleteUrl="/fuel/vehicles"
    columns={[
      { k: 'vehicle_number', label: 'Vehicle #' }, { k: 'vehicle_type', label: 'Type' },
      { k: 'ownership', label: 'Ownership' }, { k: 'fuel_type', label: 'Fuel' },
      { k: 'tank_capacity_l', label: 'Tank L', right: true },
      { k: 'mileage_benchmark_kmpl', label: 'Benchmark kmpl', right: true },
      { k: 'driver_name', label: 'Driver' }, { k: 'project_name', label: 'Location' },
      { k: 'status', label: 'Status' },
    ]}
    FormComponent={VehicleForm}
  />;
}

function VehicleForm({ existing, onClose, onSaved }) {
  const [offices, setOffices] = React.useState([]);
  const [drivers, setDrivers] = useState([]);
  const [form, setForm] = useState(existing || {
    vehicle_number: '', vehicle_type: 'Transit Mixer', ownership: 'Own',
    fuel_type: 'Diesel', tank_capacity_l: 0, mileage_benchmark_kmpl: 0,
    driver_id: '', driver_name: '',
    office_location_id: '', office_location_name: '', status: 'Active',
  });

  const loadDrivers = () => {
    apiGet('/fuel/drivers').then(r => setDrivers(r?.items || [])).catch(() => {});
  };

  React.useEffect(() => {
    apiGet('/mobile/attendance/office-locations')
      .then((r) => setOffices(r?.items || r?.offices || []))
      .catch(() => {});
    loadDrivers();
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      const payload = { ...form };
      if (existing) {
        await apiPut(`/fuel/vehicles/${existing.id}`, payload);
      } else {
        const newVehicle = await apiPost('/fuel/vehicles', payload);
        // assign-driver needs a real vehicle id — must be called after create
        if (form.driver_id && newVehicle?.id) {
          try {
            await apiPut(`/fuel/vehicles/${newVehicle.id}/assign-driver`, { driver_id: form.driver_id });
          } catch (e) {
            toast.error(e?.response?.data?.detail?.message || 'Vehicle saved but driver assignment failed');
          }
        }
      }
      toast.success('Saved'); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <Modal title={existing ? 'Edit Vehicle' : 'New Vehicle'} onClose={onClose} onSave={save}>
      <Field label="Vehicle Number"><input value={form.vehicle_number} onChange={(e) => set('vehicle_number', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></Field>
      <Field label="Type"><select value={form.vehicle_type} onChange={(e) => set('vehicle_type', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm">{VEHICLE_TYPES.map(t => <option key={t}>{t}</option>)}</select></Field>
      <Field label="Ownership"><select value={form.ownership} onChange={(e) => set('ownership', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"><option>Own</option><option>Hired</option></select></Field>
      <Field label="Fuel Type"><select value={form.fuel_type} onChange={(e) => set('fuel_type', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm">{FUEL_TYPES.map(t => <option key={t}>{t}</option>)}</select></Field>
      <Field label="Tank Capacity (L)"><input type="number" value={form.tank_capacity_l} onChange={(e) => set('tank_capacity_l', Number(e.target.value))} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></Field>
      <Field label="Mileage Benchmark (kmpl)"><input type="number" step="0.1" value={form.mileage_benchmark_kmpl} onChange={(e) => set('mileage_benchmark_kmpl', Number(e.target.value))} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></Field>
      <Field label="Driver">
        <select
          value={form.driver_id || ''}
          onChange={async (e) => {
            const newDriverId = e.target.value || null;
            const matchedDriver = drivers.find(d => d.id === newDriverId);
            set('driver_id', newDriverId);
            set('driver_name', matchedDriver?.name || '');
            // For existing vehicles: persist assignment immediately so
            // the 1-driver-1-vehicle rule runs and previous assignment clears
            if (existing?.id) {
              try {
                await apiPut(`/fuel/vehicles/${existing.id}/assign-driver`, { driver_id: newDriverId });
                loadDrivers();
              } catch (err) {
                toast.error(err?.response?.data?.detail?.message || 'Could not assign driver');
              }
            }
          }}
          className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white"
          data-testid="fuel-vehicle-driver-select"
        >
          <option value="">— Unassigned —</option>
          {drivers.map(d => (
            <option
              key={d.id}
              value={d.id}
              disabled={!!(d.current_vehicle_id && d.current_vehicle_id !== (existing?.id || form.id))}
            >
              {d.name}{d.current_vehicle_id && d.current_vehicle_id !== (existing?.id || form.id)
                ? ` (assigned to ${d.current_vehicle_number || 'another vehicle'})`
                : ''}
            </option>
          ))}
          {drivers.length === 0 && (
            <option disabled>No drivers yet — add in Driver List</option>
          )}
        </select>
      </Field>
      {/* Location — single-plant RMC has no Project entity, only office/plant locations */}
      <Field label="Assigned To">
        <select value={form.office_location_id || ''} onChange={(e) => {
            const sel = offices.find(o => o.id === e.target.value);
            set('office_location_id', e.target.value);
            set('office_location_name', sel?.name || '');
          }} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white" data-testid="fuel-vehicle-office">
            <option value="">— Select location —</option>
            {offices.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
      </Field>
      <GpsSection form={form} set={set} existing={existing} />
    </Modal>
  );
}


export function EquipmentMaster() {
  return <MasterTable
    title="Equipment Master" icon={<Wrench size={18} />}
    fetchUrl="/fuel/equipment" deleteUrl="/fuel/equipment"
    columns={[
      { k: 'equipment_id', label: 'Equip ID' }, { k: 'equipment_type', label: 'Type' },
      { k: 'make_model', label: 'Make/Model' }, { k: 'ownership', label: 'Ownership' },
      { k: 'fuel_benchmark_lph', label: 'Benchmark L/hr', right: true },
      { k: 'hour_meter', label: 'Hours', right: true },
      { k: 'operator_name', label: 'Operator' }, { k: 'project_name', label: 'Location' },
      { k: 'status', label: 'Status' },
    ]}
    FormComponent={EquipmentForm}
  />;
}

function EquipmentForm({ existing, onClose, onSaved }) {
  const [offices, setOffices] = React.useState([]);
  const [form, setForm] = useState(existing || {
    equipment_id: '', equipment_type: 'Batching Plant', make_model: '', ownership: 'Own',
    fuel_type: 'Diesel', tank_capacity_l: 500, fuel_benchmark_lph: 0, hour_meter: 0,
    operator_name: '',
    office_location_id: '', office_location_name: '', status: 'Active',
  });

  React.useEffect(() => {
    apiGet('/mobile/attendance/office-locations')
      .then((r) => setOffices(r?.items || r?.offices || []))
      .catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      const payload = { ...form };
      if (existing) await apiPut(`/fuel/equipment/${existing.id}`, payload);
      else await apiPost('/fuel/equipment', payload);
      toast.success('Saved'); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <Modal title={existing ? 'Edit Equipment' : 'New Equipment'} onClose={onClose} onSave={save}>
      <Field label="Equipment ID"><input value={form.equipment_id} onChange={(e) => set('equipment_id', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></Field>
      <Field label="Type"><select value={form.equipment_type} onChange={(e) => set('equipment_type', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm">{EQUIPMENT_TYPES.map(t => <option key={t}>{t}</option>)}</select></Field>
      <Field label="Make / Model"><input value={form.make_model || ''} onChange={(e) => set('make_model', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></Field>
      <Field label="Ownership"><select value={form.ownership} onChange={(e) => set('ownership', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"><option>Own</option><option>Hired</option></select></Field>
      <Field label="Fuel Benchmark (L/hr)"><input type="number" step="0.1" value={form.fuel_benchmark_lph} onChange={(e) => set('fuel_benchmark_lph', Number(e.target.value))} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></Field>
      <Field label="Hour Meter (current)"><input type="number" value={form.hour_meter} onChange={(e) => set('hour_meter', Number(e.target.value))} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></Field>
      <Field label="Operator Name"><input value={form.operator_name || ''} onChange={(e) => set('operator_name', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></Field>
      {/* Location — single-plant RMC has no Project entity, only office/plant locations */}
      <Field label="Assigned To">
        <select value={form.office_location_id || ''} onChange={(e) => {
            const sel = offices.find(o => o.id === e.target.value);
            set('office_location_id', e.target.value);
            set('office_location_name', sel?.name || '');
          }} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white" data-testid="fuel-equipment-office">
            <option value="">— Select location —</option>
            {offices.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
      </Field>
    </Modal>
  );
}


function Modal({ title, onClose, onSave, children }) {
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-3">
      <div className="bg-white rounded-md w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white">
          <h3 className="font-display font-bold text-lg">{title}</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200">
          <button onClick={onClose} className="px-3 py-1.5 text-sm">Cancel</button>
          <button onClick={onSave} className="px-4 py-1.5 bg-[#1F7A6E] hover:bg-[#14504E] text-white rounded-sm text-sm font-display font-bold">Save</button>
        </div>
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────
// 2.4 Refuelling Log
// ────────────────────────────────────────────────────────────
export function RefuellingLog() {
  const { useAuth } = require('../../contexts/AuthContext');
  const { user } = useAuth?.() || {};
  const isSuperAdmin = (user?.role || '').toLowerCase() === 'super_admin';
  const [rows, setRows] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [logDetail, setLogDetail] = useState({});   // id → full log data
  const [showForm, setShowForm] = useState(false);
  const [fuelTypes, setFuelTypes] = useState([]);
  const [filters, setFilters] = useState({ kind: '', fuel_type: '', date_from: '', date_to: '' });

  const refresh = (f = filters) => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    apiGet('/fuel/logs', params).then(d => setRows(d.items || []));
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filters]);
  useEffect(() => { apiGet('/fuel/fuel-types').then(d => setFuelTypes(d.values || [])).catch(() => {}); }, []);

  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const toggleExpand = async (row) => {
    if (expandedId === row.id) { setExpandedId(null); return; }
    setExpandedId(row.id);
    if (!logDetail[row.id]) {
      try {
        const d = await apiGet(`/fuel/logs/${row.id}`);
        setLogDetail(prev => ({ ...prev, [row.id]: d }));
      } catch {}
    }
  };

  const deleteLog = async (id) => {
    if (!window.confirm('Delete this fuel log? This cannot be undone.')) return;
    try {
      await apiDel(`/fuel/logs/${id}`);
      toast.success('Log deleted');
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([, v]) => v)));
      const r = await api.get(`/fuel/logs/export.csv?${params}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'fuel-logs.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const PHOTO_SLOTS = [
    { slot: 'meter_zero', label: 'Meter at 0' },
    { slot: 'vehicle', label: 'Vehicle' },
    { slot: 'meter_final', label: 'Final Meter' },
    { slot: 'pump_bill', label: 'Pump Bill' },
    { slot: 'driver_selfie', label: 'Driver Selfie' },
  ];

  return (
    <div className="p-6 space-y-4">
      <PageHeader title="Refuelling Log" icon={<Activity size={18} />}
        actions={(
          <div className="flex items-center gap-2">
            <button onClick={exportCsv} data-testid="fuel-export-csv-btn" className="px-3 py-1.5 border border-slate-300 hover:border-slate-400 rounded-sm text-xs font-display font-bold flex items-center gap-1.5"><Download size={13} /> Export CSV</button>
            <button onClick={() => setShowForm(true)} className="px-3 py-1.5 bg-[#1F7A6E] hover:bg-[#14504E] text-white rounded-sm text-xs font-display font-bold flex items-center gap-1.5"><Plus size={14} /> New Log</button>
          </div>
        )}
      />
      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="fuel-log-filters">
        <select value={filters.kind} onChange={(e) => setF('kind', e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-sm bg-white">
          <option value="">All kinds</option><option value="vehicle">Vehicle</option><option value="equipment">Equipment</option><option value="tank_refill">Tank Refill</option>
        </select>
        <select value={filters.fuel_type} onChange={(e) => setF('fuel_type', e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-sm bg-white" data-testid="fuel-filter-type">
          <option value="">All fuel types</option>
          {fuelTypes.map(t => <option key={t}>{t}</option>)}
        </select>
        <input type="date" value={filters.date_from} onChange={(e) => setF('date_from', e.target.value)} className="px-2 py-1 border border-slate-300 rounded-sm" />
        <span className="text-slate-400">→</span>
        <input type="date" value={filters.date_to} onChange={(e) => setF('date_to', e.target.value)} className="px-2 py-1 border border-slate-300 rounded-sm" />
        {(filters.kind || filters.fuel_type || filters.date_from || filters.date_to) && (
          <button onClick={() => setFilters({ kind: '', fuel_type: '', date_from: '', date_to: '' })} className="text-[#1F7A6E] hover:underline">Clear</button>
        )}
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-display">
            <tr>
              <th className="px-3 py-2 w-6"></th>
              <th className="px-3 py-2 text-left">When</th><th className="px-3 py-2 text-left">Kind</th>
              <th className="px-3 py-2 text-left">Asset</th><th className="px-3 py-2 text-left">Location</th>
              <th className="px-3 py-2 text-right">Litres</th><th className="px-3 py-2 text-right">Rate</th>
              <th className="px-3 py-2 text-right">Amount</th><th className="px-3 py-2 text-left">By</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const detail = logDetail[r.id];
              const photos = detail?.evidence || {};
              const hasPhotos = Object.keys(photos).length > 0;
              const isOpen = expandedId === r.id;
              return (
                <React.Fragment key={r.id}>
                  <tr
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => toggleExpand(r)}
                    data-testid={`fuel-log-row-${r.id}`}
                  >
                    <td className="px-3 py-2 text-xs text-slate-400">{isOpen ? '▼' : '▶'}</td>
                    <td className="px-3 py-2 text-xs">{new Date(r.log_datetime).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}</td>
                    <td className="px-3 py-2 text-xs"><span className="px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase bg-slate-100">{r.kind}</span></td>
                    <td className="px-3 py-2 text-xs">{r.vehicle_name || r.equipment_name}</td>
                    <td className="px-3 py-2 text-xs truncate max-w-[180px]">{r.project_name || r.office_location_name || '—'}</td>
                    <td className="px-3 py-2 text-xs text-right tabular-nums">{r.fuel_quantity_l?.toFixed(1)}</td>
                    <td className="px-3 py-2 text-xs text-right tabular-nums">{r.rate_per_l?.toFixed(2)}</td>
                    <td className="px-3 py-2 text-xs text-right tabular-nums font-bold">{formatINR(r.total_amount)}</td>
                    <td className="px-3 py-2 text-xs">{r.operator_or_driver || r.created_by}</td>
                    <td className="px-3 py-2 text-xs" onClick={e => e.stopPropagation()}>
                      {isSuperAdmin && (
                        <button onClick={() => deleteLog(r.id)} className="text-[10px] text-rose-600 hover:underline" data-testid={`fuel-log-delete-${r.id}`}>Delete</button>
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="border-t border-slate-100 bg-slate-50">
                      <td colSpan="10" className="px-4 py-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Log details */}
                          <div className="space-y-1 text-xs">
                            <div className="font-bold text-slate-600 mb-2">Log Details</div>
                            {detail ? (
                              <>
                                <div><span className="text-slate-400 w-32 inline-block">Fuel Type:</span> {detail.fuel_type || '—'}</div>
                                <div><span className="text-slate-400 w-32 inline-block">Odometer:</span> {detail.odometer_reading ? `${detail.odometer_reading} km` : '—'}</div>
                                <div><span className="text-slate-400 w-32 inline-block">Hour Meter:</span> {detail.hour_meter_reading ? `${detail.hour_meter_reading} hrs` : '—'}</div>
                                <div><span className="text-slate-400 w-32 inline-block">Fuel Station:</span> {detail.fuel_station || '—'}</div>
                                <div><span className="text-slate-400 w-32 inline-block">Payment:</span> {detail.payment_mode || '—'}</div>
                                {detail.gps_lat && <div><span className="text-slate-400 w-32 inline-block">GPS:</span> {detail.gps_lat?.toFixed(4)}, {detail.gps_lng?.toFixed(4)}</div>}
                                {detail.remarks && <div><span className="text-slate-400 w-32 inline-block">Remarks:</span> {detail.remarks}</div>}
                              </>
                            ) : <div className="text-slate-400">Loading…</div>}
                          </div>
                          {/* Photos */}
                          <div>
                            <div className="font-bold text-slate-600 mb-2 text-xs">Evidence Photos</div>
                            {!detail ? (
                              <div className="text-xs text-slate-400">Loading…</div>
                            ) : !hasPhotos ? (
                              <div className="text-xs text-slate-400 italic">No photos captured for this log.</div>
                            ) : (
                              <div className="grid grid-cols-2 gap-2">
                                {PHOTO_SLOTS.map(({ slot, label }) =>
                                  photos[slot] ? (
                                    <div key={slot} className="space-y-1">
                                      <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
                                      <img
                                        src={`${process.env.REACT_APP_BACKEND_URL}/api/fuel/logs/${r.id}/evidence/${slot}`}
                                        alt={label}
                                        className="w-full rounded-sm border border-slate-200 object-cover"
                                        style={{ maxHeight: 120 }}
                                        onError={e => { e.target.style.display = 'none'; }}
                                        data-testid={`fuel-log-photo-${slot}`}
                                      />
                                    </div>
                                  ) : null
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {!rows.length && <tr><td colSpan="10" className="text-center text-slate-400 italic py-6 text-sm">No fuel logs yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {showForm && <FuelLogForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); refresh(); }} />}
    </div>
  );
}

function FuelLogForm({ onClose, onSaved }) {
  const [vehicles, setVehicles] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [tanks, setTanks] = useState([]);
  const [fuelTypes, setFuelTypes] = useState([]);
  const [form, setForm] = useState({
    kind: 'vehicle', vehicle_id: '', equipment_id: '', tank_id: '',
    log_datetime: new Date().toISOString().slice(0, 16),
    fuel_type: '', fuel_quantity_l: 0, rate_per_l: 94, total_amount: 0, odometer_reading: 0, hour_meter_reading: 0,
    fuel_station: '', payment_mode: 'Fuel Card', remarks: '',
  });
  const [validation, setValidation] = useState(null);
  const set = (k, v) => setForm(f => {
    const next = { ...f, [k]: v };
    if (k === 'fuel_quantity_l' || k === 'rate_per_l') {
      next.total_amount = Number(next.fuel_quantity_l || 0) * Number(next.rate_per_l || 0);
    }
    return next;
  });
  useEffect(() => {
    apiGet('/fuel/vehicles').then(d => setVehicles(d.items || []));
    apiGet('/fuel/equipment').then(d => setEquipment(d.items || []));
    apiGet('/fuel/tanks').then(d => setTanks(d.items || []));
    // FIX 9 — fuel types come from the configurable company master.
    apiGet('/fuel/fuel-types').then(d => setFuelTypes(d.values || [])).catch(() => {});
  }, []);
  const validate = async () => {
    try {
      const d = await apiPost('/fuel/validate-log', { ...form, log_datetime: new Date(form.log_datetime).toISOString() });
      setValidation(d);
    } catch (e) { toast.error('Validate failed'); }
  };
  const save = async (rateConfirmed = false) => {
    try {
      const res = await apiPost('/fuel/logs', { ...form, rate_confirmed: rateConfirmed, log_datetime: new Date(form.log_datetime).toISOString() });
      // FIX 2 — rate anomaly: confirm + re-submit with rate_confirmed=true.
      if (res?.rate_warning) {
        if (window.confirm(res.rate_warning)) return save(true);
        return;
      }
      toast.success('Log saved');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Save failed');
    }
  };
  return (
    <Modal title="New Refuelling Log" onClose={onClose} onSave={() => save(false)}>
      <Field label="Kind">
        <select value={form.kind} onChange={(e) => set('kind', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm">
          <option value="vehicle">Vehicle</option><option value="equipment">Equipment</option><option value="tank_refill">Tank Refill</option>
        </select>
      </Field>
      {form.kind === 'vehicle' ? (
        <Field label="Vehicle"><select value={form.vehicle_id} onChange={(e) => set('vehicle_id', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"><option value="">Select…</option>{vehicles.map(v => <option key={v.id} value={v.id}>{v.vehicle_number}</option>)}</select></Field>
      ) : form.kind === 'equipment' ? (
        <Field label="Equipment"><select value={form.equipment_id} onChange={(e) => set('equipment_id', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"><option value="">Select…</option>{equipment.map(e => <option key={e.id} value={e.id}>{e.equipment_id}</option>)}</select></Field>
      ) : <Field label=""><div className="text-xs text-slate-500 px-3 py-2">Tank refill — assign tank in the next field</div></Field>}
      <Field label="Tank (source / refill target)">
        <select value={form.tank_id} onChange={(e) => set('tank_id', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="fuel-form-tank">
          <option value="">— None —</option>
          {tanks.map(t => <option key={t.id} value={t.id}>{`${t.tank_id} · ${Math.round(t.current_level_l || 0)}L`}</option>)}
        </select>
      </Field>
      <Field label="Fuel Type">
        <select value={form.fuel_type} onChange={(e) => set('fuel_type', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="fuel-form-type">
          <option value="">— Select —</option>
          {fuelTypes.map(t => <option key={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="Date / Time"><input type="datetime-local" value={form.log_datetime} onChange={(e) => set('log_datetime', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></Field>
      <Field label="Fuel Quantity (L)"><input type="number" step="0.1" value={form.fuel_quantity_l} onChange={(e) => set('fuel_quantity_l', Number(e.target.value))} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></Field>
      <Field label="Rate per L (₹)"><input type="number" step="0.01" value={form.rate_per_l} onChange={(e) => set('rate_per_l', Number(e.target.value))} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></Field>
      <Field label="Total (₹)"><input type="number" value={form.total_amount} readOnly className="w-full px-3 py-2 border border-slate-200 bg-slate-50 rounded-sm text-sm" /></Field>
      {form.kind === 'vehicle' && <Field label="Odometer (km)"><input type="number" value={form.odometer_reading} onChange={(e) => set('odometer_reading', Number(e.target.value))} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></Field>}
      {form.kind === 'equipment' && <Field label="Hour Meter"><input type="number" value={form.hour_meter_reading} onChange={(e) => set('hour_meter_reading', Number(e.target.value))} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></Field>}
      <Field label="Station"><input value={form.fuel_station} onChange={(e) => set('fuel_station', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></Field>
      <Field label="Payment Mode"><select value={form.payment_mode} onChange={(e) => set('payment_mode', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"><option>Fuel Card</option><option>Cash</option><option>UPI</option><option>Credit Account</option></select></Field>
      <div className="md:col-span-2">
        <button onClick={validate} className="px-3 py-1.5 text-xs bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 rounded-sm flex items-center gap-1.5">
          <Gauge size={12} /> Pre-validate against benchmarks
        </button>
        {validation && (
          <ul className="mt-3 space-y-1 text-xs" data-testid="fuel-log-validation">
            {validation.validation.map((c, i) => (
              <li key={i} className="flex items-start gap-2 border-b border-slate-100 pb-1">
                <span className={`px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase ${c.status === 'pass' ? 'bg-emerald-100 text-emerald-700' : c.status === 'warning' ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`}>{c.status}</span>
                <span><strong>{c.name}</strong> — {c.detail}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}


// ────────────────────────────────────────────────────────────
// 2.5 Tank & Storage
// ────────────────────────────────────────────────────────────
const EMPTY_TANK = { tank_id: '', fuel_type: 'Diesel', capacity_l: '', current_level_l: 0, location: '', custodian: '', sensor_linked: false };

export function TankStorage() {
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // tank row being edited
  const [form, setForm] = useState(EMPTY_TANK);
  const [fuelTypes, setFuelTypes] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => apiGet('/fuel/tanks').then(d => setRows(d.items || []));
  useEffect(() => {
    load();
    apiGet('/fuel/fuel-types').then(d => setFuelTypes(d.values || [])).catch(() => {});
  }, []);

  const openAdd = () => { setForm(EMPTY_TANK); setEditing(null); setShowForm(true); };
  const openEdit = (t) => { setForm({ ...t, capacity_l: t.capacity_l ?? '', current_level_l: t.current_level_l ?? 0 }); setEditing(t); setShowForm(true); };

  const save = async () => {
    if (!form.tank_id.trim()) return toast.error('Tank ID is required');
    if (!form.capacity_l || Number(form.capacity_l) <= 0) return toast.error('Capacity must be > 0');
    setSaving(true);
    try {
      const payload = { ...form, capacity_l: Number(form.capacity_l), current_level_l: Number(form.current_level_l || 0) };
      if (editing) {
        await apiPut(`/fuel/tanks/${editing.id}`, payload);
        toast.success('Tank updated');
      } else {
        await apiPost('/fuel/tanks', payload);
        toast.success('Tank added');
      }
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="p-6 space-y-4">
      <PageHeader
        title="Tank & Storage"
        subtitle="On-site fuel tanks — capacity, level, custodian"
        icon={<Droplet size={18} />}
        actions={
          <button onClick={openAdd} className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#1F7A6E] hover:bg-[#14504E] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider" data-testid="add-tank-btn">
            <Plus size={14} /> Add Tank
          </button>
        }
      />

      {/* Add / Edit Tank form */}
      {showForm && (
        <div className="bg-white border border-slate-200 rounded-sm p-5 space-y-4" data-testid="tank-form">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-display font-bold text-sm">{editing ? 'Edit Tank' : 'Add New Tank'}</h3>
            <button onClick={() => setShowForm(false)}><X size={16} className="text-slate-400" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Tank ID *">
              <input value={form.tank_id} onChange={e => setForm(f => ({ ...f, tank_id: e.target.value }))} placeholder="e.g. TANK-001" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="tank-id-input" />
            </Field>
            <Field label="Fuel Type *">
              <select value={form.fuel_type} onChange={e => setForm(f => ({ ...f, fuel_type: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="tank-fuel-type">
                {(fuelTypes.length ? fuelTypes : ['Diesel', 'Petrol', 'HSD', 'CNG', 'LPG']).map(t => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Capacity (L) *">
              <input type="number" min="1" value={form.capacity_l} onChange={e => setForm(f => ({ ...f, capacity_l: e.target.value }))} placeholder="e.g. 5000" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="tank-capacity" />
            </Field>
            <Field label="Opening Stock (L)">
              <input type="number" min="0" value={form.current_level_l} onChange={e => setForm(f => ({ ...f, current_level_l: e.target.value }))} placeholder="0" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="tank-opening-stock" />
            </Field>
            <Field label="Location / Area">
              <input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="e.g. Site Gate, Yard" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
            </Field>
            <Field label="Custodian">
              <input value={form.custodian} onChange={e => setForm(f => ({ ...f, custodian: e.target.value }))} placeholder="Person responsible" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
            </Field>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.sensor_linked} onChange={e => setForm(f => ({ ...f, sensor_linked: e.target.checked }))} className="rounded" />
              IoT / Sensor linked
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 border border-slate-200 rounded-sm">Cancel</button>
            <button onClick={save} disabled={saving} className="px-4 py-2 text-sm font-bold bg-[#1F7A6E] hover:bg-[#14504E] text-white rounded-sm disabled:opacity-50 flex items-center gap-1.5" data-testid="tank-save-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              {editing ? 'Update Tank' : 'Add Tank'}
            </button>
          </div>
        </div>
      )}

      {/* Tank cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map(t => {
          const pct = t.capacity_l ? Math.min(100, Math.round((t.current_level_l / t.capacity_l) * 100)) : 0;
          const color = pct < 25 ? 'bg-rose-500' : pct < 50 ? 'bg-amber-500' : 'bg-emerald-500';
          return (
            <div key={t.id} className="bg-white border border-slate-200 rounded-sm p-4" data-testid={`tank-card-${t.id}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-display font-bold">{t.tank_id}</h3>
                <div className="flex items-center gap-2">
                  <span className="px-1.5 py-0.5 rounded-sm text-[10px] font-bold bg-slate-100">{t.fuel_type}</span>
                  <button onClick={() => openEdit(t)} className="text-slate-400 hover:text-[#1F7A6E] p-0.5" title="Edit tank" data-testid={`tank-edit-${t.id}`}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  </button>
                </div>
              </div>
              <div className="text-xs text-slate-500">{t.location || '—'}</div>
              <div className="h-4 bg-slate-100 rounded mt-3"><div className={`h-4 rounded ${color}`} style={{ width: `${pct}%` }} /></div>
              <div className="flex justify-between text-xs mt-1"><span><strong>{(t.current_level_l || 0).toFixed(0)} L</strong> ({pct}%)</span><span className="text-slate-400">/ {(t.capacity_l || 0).toFixed(0)} L</span></div>
              <div className="text-[10px] text-slate-400 mt-2">Custodian: {t.custodian || '—'} · Sensor: {t.sensor_linked ? 'Yes' : 'No'}</div>
            </div>
          );
        })}
        {!rows.length && !showForm && (
          <div className="text-center text-slate-400 italic py-8 text-sm md:col-span-2">
            No tanks registered yet. <button onClick={openAdd} className="text-[#1F7A6E] font-bold underline">Add the first tank →</button>
          </div>
        )}
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────
// 2.6 Fuel Alerts
// ────────────────────────────────────────────────────────────
const ALERT_TYPE_LABELS = {
  bill_authenticity: 'Bill Authenticity Doubtful',
  rate_anomaly: 'Unusual Fuel Rate',
  odometer_regression: 'Odometer Reading Went Down',
  quantity_anomaly: 'Unusual Quantity',
  night_movement: 'Night Movement Detected',
  geofence_breach: 'Refuelling Outside Authorised Area',
  duplicate_log: 'Possible Duplicate Entry',
};

const PHOTO_SLOTS_ALERT = [
  { slot: 'meter_zero', label: 'Meter at Zero' },
  { slot: 'vehicle', label: 'Vehicle' },
  { slot: 'meter_final', label: 'Final Meter' },
  { slot: 'pump_bill', label: 'Pump Bill' },
];

export function FuelAlerts() {
  const [rows, setRows] = useState([]);
  const [logDetails, setLogDetails] = useState({});  // log_id → full log

  const refresh = () => apiGet('/fuel/alerts').then(d => setRows(d.items || []));
  useEffect(() => { refresh(); }, []);

  // Load fuel log evidence for each alert
  useEffect(() => {
    rows.forEach(a => {
      if (a.log_id && !logDetails[a.log_id]) {
        apiGet(`/fuel/logs/${a.log_id}`).then(d => {
          setLogDetails(prev => ({ ...prev, [a.log_id]: d }));
        }).catch(() => {});
      }
    });
  }, [rows]);  // eslint-disable-line

  const update = async (id, status) => {
    try { await apiPut(`/fuel/alerts/${id}`, { status }); toast.success('Updated'); refresh(); }
    catch { toast.error('Failed'); }
  };

  const addNote = async (id) => {
    const note = window.prompt('Investigator note:');
    if (!note) return;
    try { await apiPut(`/fuel/alerts/${id}`, { investigator_note: note }); toast.success('Note saved'); refresh(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="p-6 space-y-4">
      <PageHeader title="Fuel Fraud Alerts"
        subtitle="AI-flagged anomalies — review each entry and resolve"
        icon={<AlertTriangle size={18} />} />
      <div className="space-y-4" data-testid="fuel-alerts-list">
        {rows.map(a => {
          const log = logDetails[a.log_id];
          const evidence = log?.evidence || {};
          const hasPhotos = Object.keys(evidence).length > 0;
          const isOpen = a.status !== 'Resolved';
          return (
            <div key={a.id} className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid={`fuel-alert-${a.id}`}>
              {/* Header */}
              <div className="flex items-start gap-3 px-4 py-3 border-b border-slate-100">
                <span className={`shrink-0 mt-0.5 px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase ${a.severity === 'high' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>
                  {a.severity}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-sm">
                    {ALERT_TYPE_LABELS[a.type] || (a.type || '').replaceAll('_', ' ')}
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">{a.explanation}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5 flex flex-wrap gap-2">
                    <span>{new Date(a.created_at).toLocaleString('en-IN')}</span>
                    <span>AI confidence: {a.ai_confidence}%</span>
                    {log && <span>Vehicle: {log.vehicle_name || log.vehicle_id?.slice(0,8)}</span>}
                    {log && <span>{log.fuel_quantity_l?.toFixed(1)} L · ₹{log.total_amount?.toLocaleString('en-IN')}</span>}
                  </div>
                  {a.investigator_note && (
                    <div className="mt-1.5 text-xs bg-amber-50 border border-amber-200 rounded px-2 py-1 text-amber-800">
                      Note: {a.investigator_note}
                    </div>
                  )}
                </div>
                <span className={`shrink-0 px-1.5 py-0.5 rounded-sm text-[10px] font-bold ${
                  a.status === 'Resolved' ? 'bg-emerald-100 text-emerald-700' :
                  'bg-amber-100 text-amber-700'}`}>
                  {a.status}
                </span>
              </div>

              {/* Photos from the linked fuel log */}
              <div className="px-4 py-3">
                {!log ? (
                  <div className="text-xs text-slate-400">Loading log details…</div>
                ) : !hasPhotos ? (
                  <div className="text-xs text-slate-400 italic">No evidence photos were captured for this fuel log.</div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                    {PHOTO_SLOTS_ALERT.map(({ slot, label }) =>
                      evidence[slot] ? (
                        <div key={slot}>
                          <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold mb-1">{label}</div>
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}/api/fuel/logs/${a.log_id}/evidence/${slot}`}
                            alt={label}
                            className="w-full rounded-sm border border-slate-200 object-cover cursor-pointer hover:opacity-90"
                            style={{ maxHeight: 100 }}
                            onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/fuel/logs/${a.log_id}/evidence/${slot}`, '_blank')}
                            onError={e => { e.target.parentElement.style.display='none'; }}
                            data-testid={`alert-photo-${slot}`}
                          />
                          {evidence[slot].checked === false && (
                            <div className="text-[9px] text-rose-600 mt-0.5 font-bold">⚠ Unchecked</div>
                          )}
                        </div>
                      ) : null
                    )}
                  </div>
                )}

                {/* Actions — single Resolve button, matching unified FraudAlerts page */}
                {isOpen && (
                  <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-slate-100">
                    <button onClick={() => addNote(a.id)}
                      className="text-[11px] px-2.5 py-1.5 border border-slate-300 rounded hover:bg-slate-50">
                      Add Note
                    </button>
                    <button onClick={() => update(a.id, 'Resolved')}
                      className="text-[11px] px-2.5 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 font-bold"
                      data-testid="fuel-fa-resolve">
                      Resolve
                    </button>
                  </div>
                )}
                {!isOpen && (
                  <div className="pt-2 border-t border-slate-100 text-xs text-slate-400">
                    ✓ Resolved.{a.updated_by && ` By ${a.updated_by}.`}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {!rows.length && <div className="text-center text-slate-400 italic py-8 text-sm">No fuel alerts. All clear.</div>}
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────
// 2.7 Driver / Operator Scores
// ────────────────────────────────────────────────────────────
export function DriverScores() {
  const [rows, setRows] = useState([]);
  useEffect(() => { apiGet('/fuel/driver-scores').then(d => setRows(d.items || [])); }, []);
  return (
    <div className="p-6 space-y-4">
      <PageHeader title="Driver / Operator Scores" subtitle="Last 30 days · efficiency & violations" icon={<UsersRound size={18} />} />
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-display">
            <tr><th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2 text-right">Logs</th>
              <th className="px-3 py-2 text-right">Total L</th><th className="px-3 py-2 text-right">Total ₹</th>
              <th className="px-3 py-2 text-right">Alerts</th><th className="px-3 py-2 text-center">Grade</th>
              <th className="px-3 py-2 text-right">Score</th></tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.name} className="border-t border-slate-100">
                <td className="px-3 py-2 font-display font-semibold">{r.name}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.logs}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.total_litres?.toFixed(0)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatINR(r.total_amount)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.alerts_triggered}</td>
                <td className="px-3 py-2 text-center"><span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold ${r.grade === 'A' ? 'bg-emerald-100 text-emerald-700' : r.grade === 'B' ? 'bg-lime-100 text-lime-700' : r.grade === 'C' ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`}>{r.grade}</span></td>
                <td className="px-3 py-2 text-right font-bold tabular-nums">{r.score}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan="7" className="text-center text-slate-400 italic py-6 text-sm">No scores yet — operate the equipment first.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────
// 2.8 Reports — Phase 3C (PDF + Excel exports)
// ────────────────────────────────────────────────────────────
export function FuelDriverList() {
  const [drivers, setDrivers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [empSearch, setEmpSearch] = useState('');
  const [empResults, setEmpResults] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [licenseForm, setLicenseForm] = useState({ license_number: '', license_expiry: '', license_class: 'LMV', notes: '' });
  const [saving, setSaving] = useState(false);

  const load = () => apiGet('/fuel/drivers', { active_only: false }).then(d => setDrivers(d.items || []));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!empSearch.trim()) { setEmpResults([]); return; }
    apiGet('/fuel/drivers/eligible-employees', { search: empSearch }).then(r => setEmpResults(r.items || []));
  }, [empSearch]);

  const addDriver = async () => {
    if (!selectedEmp) return toast.error('Select an employee first');
    setSaving(true);
    try {
      await apiPost('/fuel/drivers', { employee_id: selectedEmp.id, ...licenseForm });
      toast.success(`${selectedEmp.name} added to Driver List`);
      setShowAdd(false); setSelectedEmp(null); setEmpSearch(''); setEmpResults([]);
      setLicenseForm({ license_number: '', license_expiry: '', license_class: 'LMV', notes: '' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const toggleActive = async (d) => {
    try {
      await apiPut(`/fuel/drivers/${d.id}`, { ...d, is_active: !d.is_active, employee_id: d.employee_id });
      load();
    } catch { toast.error('Failed'); }
  };

  const removeDriver = async (id, name) => {
    if (!window.confirm(`Remove ${name} from the Driver List?`)) return;
    try { await apiDel(`/fuel/drivers/${id}`); load(); } catch { toast.error('Failed'); }
  };

  return (
    <div className="p-6 space-y-4">
      <PageHeader title="Driver List" subtitle="All authorised drivers — any driver can fuel any vehicle"
        icon={<UsersRound size={18} />}
        actions={<button onClick={() => setShowAdd(true)} className="flex items-center gap-1.5 px-3 py-2 bg-[#1F7A6E] text-white rounded-sm text-xs font-display font-bold"><Plus size={14} /> Add Driver</button>}
      />

      {showAdd && (
        <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3" data-testid="driver-add-form">
          <div className="font-display font-bold text-sm">Add Employee as Driver</div>
          <div className="relative">
            <input value={empSearch} onChange={e => setEmpSearch(e.target.value)}
              placeholder="Search employee by name…"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
            {empResults.length > 0 && (
              <div className="absolute z-20 w-full bg-white border border-slate-200 rounded-sm shadow-lg max-h-48 overflow-y-auto mt-0.5">
                {empResults.map(e => (
                  <button key={e.id} type="button" onMouseDown={() => { setSelectedEmp(e); setEmpSearch(e.name); setEmpResults([]); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 border-b border-slate-50">
                    <div>
                      <span className="font-medium">{e.name}</span>
                      {e.designation && <span className="text-[10px] ml-1.5 text-slate-400">{e.designation}</span>}
                      {(e.designation || '').toLowerCase().includes('driver') && (
                        <span className="text-[9px] ml-1.5 bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded-full font-bold">DRIVER</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {selectedEmp && (
            <div className="text-xs bg-slate-50 rounded-sm px-3 py-2 text-slate-700">
              Selected: <strong>{selectedEmp.name}</strong> · {selectedEmp.designation} · {selectedEmp.emp_id}
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div><label className="text-slate-500 font-medium">License No.</label>
              <input value={licenseForm.license_number} onChange={e => setLicenseForm(f => ({...f, license_number: e.target.value}))} className="w-full mt-0.5 px-2 py-1.5 border border-slate-300 rounded-sm text-sm" /></div>
            <div><label className="text-slate-500 font-medium">Expiry</label>
              <input type="date" value={licenseForm.license_expiry} onChange={e => setLicenseForm(f => ({...f, license_expiry: e.target.value}))} className="w-full mt-0.5 px-2 py-1.5 border border-slate-300 rounded-sm text-sm" /></div>
            <div><label className="text-slate-500 font-medium">Class</label>
              <select value={licenseForm.license_class} onChange={e => setLicenseForm(f => ({...f, license_class: e.target.value}))} className="w-full mt-0.5 px-2 py-1.5 border border-slate-300 rounded-sm text-sm bg-white">
                {['LMV','HMV','Transport','MCWG','LMV-TR'].map(c => <option key={c}>{c}</option>)}
              </select></div>
            <div><label className="text-slate-500 font-medium">Notes</label>
              <input value={licenseForm.notes} onChange={e => setLicenseForm(f => ({...f, notes: e.target.value}))} className="w-full mt-0.5 px-2 py-1.5 border border-slate-300 rounded-sm text-sm" /></div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => { setShowAdd(false); setSelectedEmp(null); setEmpSearch(''); }}
              className="px-3 py-1.5 text-xs border border-slate-300 rounded-sm">Cancel</button>
            <button onClick={addDriver} disabled={saving || !selectedEmp}
              className="px-3 py-1.5 text-xs bg-[#1F7A6E] text-white rounded-sm disabled:opacity-40 font-bold" data-testid="driver-add-save">
              {saving ? 'Saving…' : 'Add Driver'}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2 text-left">Emp ID</th>
              <th className="px-3 py-2 text-left">License</th><th className="px-3 py-2 text-left">Expiry</th>
              <th className="px-3 py-2 text-left">Assigned Vehicle</th><th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {drivers.map(d => (
              <tr key={d.id} className={`border-t border-slate-100 ${d.is_active ? '' : 'opacity-50'}`} data-testid={`driver-row-${d.id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium">{d.name}</div>
                  {(d.designation || '').toLowerCase().includes('driver') && (
                    <span className="text-[9px] bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded-full font-bold">DRIVER</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs font-mono text-slate-500">{d.emp_id}</td>
                <td className="px-3 py-2 text-xs">{d.license_number || '—'}{d.license_class && <span className="ml-1 text-[9px] text-slate-400">{d.license_class}</span>}</td>
                <td className="px-3 py-2 text-xs">{d.license_expiry || '—'}</td>
                <td className="px-3 py-2 text-xs">{d.current_vehicle_number || <span className="text-slate-400">Unassigned</span>}</td>
                <td className="px-3 py-2 text-xs">
                  <button onClick={() => toggleActive(d)} className={`px-2 py-0.5 text-[10px] rounded-full font-bold ${d.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    {d.is_active ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td className="px-3 py-2">
                  <button onClick={() => removeDriver(d.id, d.name)} className="text-[10px] text-rose-600 hover:underline" data-testid={`driver-remove-${d.id}`}>Remove</button>
                </td>
              </tr>
            ))}
            {!drivers.length && <tr><td colSpan="7" className="text-center text-slate-400 italic py-6 text-sm">No drivers registered yet. Add employees from the button above.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function FuelReports() {
  const [items, setItems] = useState([]);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [busy, setBusy] = useState(null);
  useEffect(() => { apiGet('/fuel/reports').then(d => setItems(d.items || [])).catch(() => {}); }, []);

  const download = async (id, fmt) => {
    const key = `${id}-${fmt}`;
    setBusy(key);
    try {
      const resp = await api.get(`/fuel/reports/${id}/export`, {
        params: { format: fmt, month },
        responseType: 'blob',
        timeout: 60000,
      });
      const blob = new Blob([resp.data], { type: resp.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${id}_${month}.${fmt}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="p-6 space-y-4">
      <PageHeader title="Fuel Reports" subtitle="Exportable PDF / Excel" icon={<PieChart size={18} />} />
      <div className="bg-white border border-slate-200 rounded-sm p-3 flex items-center gap-3">
        <label className="text-[10px] uppercase tracking-widest font-display text-slate-500">Period</label>
        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="px-3 py-1.5 border border-slate-300 rounded-sm text-sm"
          data-testid="fuel-reports-month"
        />
        <div className="text-xs text-slate-500 ml-auto">{items.length} reports available</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="fuel-reports-grid">
        {items.map((r) => (
          <div key={r.id} className="bg-white border border-slate-200 rounded-sm p-4 flex flex-col">
            <FileText size={18} className="text-[#1F7A6E] mb-2" />
            <div className="font-display font-bold text-sm">{r.title}</div>
            <div className="text-[10px] text-slate-400 mt-1 flex-1">Period: {month}</div>
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={() => download(r.id, 'pdf')}
                disabled={busy === `${r.id}-pdf`}
                className="flex-1 px-2 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider bg-[#1F7A6E] hover:bg-[#14504E] text-white rounded-sm disabled:opacity-50 flex items-center justify-center gap-1.5"
                data-testid={`fuel-report-${r.id}-pdf`}
              >
                {busy === `${r.id}-pdf` ? <Loader2 size={11} className="animate-spin" /> : null} PDF
              </button>
              <button
                onClick={() => download(r.id, 'xlsx')}
                disabled={busy === `${r.id}-xlsx`}
                className="flex-1 px-2 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider border border-[#1F7A6E] text-[#1F7A6E] hover:bg-[#1F7A6E] hover:text-white rounded-sm disabled:opacity-50 flex items-center justify-center gap-1.5"
                data-testid={`fuel-report-${r.id}-xlsx`}
              >
                {busy === `${r.id}-xlsx` ? <Loader2 size={11} className="animate-spin" /> : null} Excel
              </button>
            </div>
          </div>
        ))}
        {!items.length && <div className="col-span-full text-center text-slate-400 italic py-8 text-sm">No reports configured.</div>}
      </div>
    </div>
  );
}


export default FuelDashboard;
